<template>
  <ExtendedModal
    :open="isOpen"
    :aria-label="shown?.label"
    @close="handleClose()"
  >
    <ContentSwap>
      <DetailLayout
        v-if="shown?.kind === 'letter'"
        key="mp-letter"
        @close="handleClose()"
      >
        <template #header>
          <h2 class="raw-heading-2">
            {{ i18n(letterTranslations.title) }}
          </h2>
        </template>

        <MpLetter />
      </DetailLayout>

      <DetailLayout v-else key="action" @close="handleClose()">
        <!-- The slot, not `title`/`icon`: the prop path renders `raw-heading-3`, which shrinks at md. -->
        <template #header>
          <div class="flex items-center gap-8">
            <RevIllustration
              src="/mine-my-phone/icons/heart--flat.svg"
              :width="26"
              :height="26"
              alt=""
              aria-hidden="true"
            />
            <h2 class="raw-heading-2">
              {{ i18n(translations.cta) }}
            </h2>
          </div>
        </template>

        <TakeActionContent />
      </DetailLayout>
    </ContentSwap>
  </ExtendedModal>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'

import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { RevIllustration } from '@ds/components/Illustration'

import ContentSwap from '~/scopes/campaign/components/UI/ContentSwap.vue'
import DetailLayout from '~/scopes/campaign/components/UI/DetailLayout.vue'
import ExtendedModal from '~/scopes/campaign/components/UI/ExtendedModal.vue'
import MpLetter from '~/scopes/campaign/components/takeAction/MpLetter.vue'
import TakeActionContent from '~/scopes/campaign/components/takeAction/TakeActionContent.vue'
import { useModalState } from '~/scopes/campaign/composables/useModalState'
import { MODAL_ROUTES } from '~/scopes/campaign/const/config.const'

import letterTranslations from './MpLetter.translations'
import translations from './TakeAction.translations'

// One take-action surface for the header pill and every `TakeActionCallout`: `?action`
// layers over whatever is mounted, `/take-action` is the deep-linkable address.
//
// Two branches share one card, the `ExtendedReader` shape: separate adapters would
// cross-dissolve two `blur(80px)` backdrops, and a `backdrop-filter` under opacity < 1 is
// inert (`.claude/rules/article-extended.md` §Transitions).
const i18n = useI18n()
const { activeModal, activeAction, activeMpLetter, closeView, closeToImpacts } =
  useModalState()

const isOpen = computed(
  () =>
    activeAction.value ||
    activeMpLetter.value ||
    activeModal.value === MODAL_ROUTES.TAKE_ACTION,
)

type Branch = { kind: 'action' | 'letter'; label: string; pageView: string }

// The param wins over the route, so `/take-action?view=mp-letter` shows the letter.
const branch = computed<Branch | null>(() => {
  if (!isOpen.value) return null
  if (activeMpLetter.value)
    return {
      kind: 'letter',
      label: i18n(letterTranslations.title),
      pageView: 'take-action-mp-letter',
    }

  return {
    kind: 'action',
    label: i18n(translations.cta),
    pageView: 'take-action',
  }
})

// Closing drops `?view` on the first frame of a 400ms fade, so this holds the last branch
// through the leave rather than emptying the card mid-flight.
const shown = ref(branch.value)
watch(branch, (value) => {
  if (value) shown.value = value
})

function handleClose() {
  if (activeModal.value === MODAL_ROUTES.TAKE_ACTION) closeToImpacts()
  else closeView()
}
</script>
